import { DATE_FORMAT } from './constants';
import countryCodes from './countryCodes.json';

import type { CountryCode } from 'libphonenumber-js';

const BDAY_INPUT_YEAR_THRESHOLD = 1900;
const nowMs = Date.now();
const year1900ms = new Date(`${BDAY_INPUT_YEAR_THRESHOLD}`).getTime();

const dateFormatMap = {
    [DATE_FORMAT.DEFAULT_EU]: {
        day: 1,
        month: 2,
        year: 3,
        separator: '.',
    },
    [DATE_FORMAT.DEFAULT_US]: { day: 2, month: 1, year: 3, separator: '/' },
    [DATE_FORMAT.MIXED]: { day: 1, month: 2, year: 3, separator: '/' },
};

export const getDateFormatObject = (dateFormat: string) => {
    const dateFormatObj = dateFormatMap[dateFormat];

    return dateFormatObj || dateFormatMap[DATE_FORMAT.DEFAULT_EU];
};

const isValidDateInput = (input: string) => {
    const inputAsNum = Number(input);

    return !isNaN(inputAsNum) && !input.includes(' ') && !input.includes('.');
};

export const formatDateMonthInput = (type: string, input: string, prevValue: string) => {
    const maxValue = type === 'day' ? 31 : 12;
    const maxDecimal = Math.floor(maxValue / 10);

    const inputAsNum = Number(input);

    if (!isValidDateInput(input)) {
        return prevValue;
    }

    if (input.length === 1 && inputAsNum > maxDecimal) {
        return `0${input}`;
    }

    if (input.length === 2 && inputAsNum > maxValue) {
        return `${maxValue}`;
    }

    return `${input}`;
};

export const formatYear = (input: string, prevValue: string) => {
    if (!isValidDateInput(input)) {
        return prevValue;
    }

    return `${input}`;
};

export const dateInputsEmpty = ({
    day,
    month,
    year,
}: {
    day: string;
    month: string;
    year: string;
}) => !day && !month && !year;

export const fullDateEntered = ({
    day,
    month,
    year,
}: {
    day: string;
    month: string;
    year: string;
}) => day.toString().length === 2 && month.toString().length === 2 && year.toString().length === 4;

const getIsDateInvalid = (dateEntered: Date, month: number) => {
    // Check date/month overflow, e.g. 31st of Feb
    if (dateEntered.getMonth() !== month - 1) {
        return true;
    }

    // Check year threshold
    if (dateEntered.getFullYear() < BDAY_INPUT_YEAR_THRESHOLD) {
        return true;
    }

    const dateSelectedMs = dateEntered.getTime();
    const dateIsInFuture = dateSelectedMs > nowMs;
    const dateIsTooFarBehind = dateSelectedMs < year1900ms;

    return dateIsInFuture || dateIsTooFarBehind;
};

export const validateDateInput = (
    {
        day,
        month,
        year,
    }: {
        day: string;
        month: string;
        year: string;
    },
    touched: boolean,
) => {
    if (fullDateEntered({ day, month, year })) {
        const dateSelected = new Date(Number(year), Number(month) - 1, Number(day));

        return isNaN(dateSelected.getTime()) || getIsDateInvalid(dateSelected, Number(month));
    }

    return touched;
};

export const isDayFocusable = (dayEl: HTMLDivElement) =>
    dayEl.className.indexOf('prevMonthDay') < 0 && dayEl.className.indexOf('nextMonthDay') < 0;

export const isDayDisabled = (dayEl: HTMLDivElement) =>
    dayEl.className.indexOf('flatpickr-disabled') >= 0 ||
    dayEl.className.indexOf('prevMonthDay') >= 0 ||
    dayEl.className.indexOf('nextMonthDay') >= 0;

export const getFlagEmoji = (countryCode: string) => {
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map((char) => 127397 + char.charCodeAt(0));

    return String.fromCodePoint(...codePoints);
};

export const parseCountryCodesInPartialNumber = (value: string) => {
    const { normalised } = normalisePhoneCodePrefix(value);

    const checkSlice = (length: number) => {
        const slice = normalised.slice(0, length + 1);
        const countryCodeList = (countryCodes as { [key: string]: string[] })[slice];

        if (countryCodeList) {
            return {
                countryCodes: countryCodeList as CountryCode[],
                phoneCode: slice,
                isPhoneCodeOnly: slice === normalised.trim(),
            };
        }
    };

    for (let i = 1; i <= 3; i++) {
        const result = checkSlice(i);

        if (result) {
            return result;
        }
    }
};

export const normalisePhoneCodePrefix = (value: string) => {
    const needsNormalisation = value.startsWith('00');
    const normalised = needsNormalisation ? `+${value.slice(2)}` : value;

    return {
        normalised,
        wasChanged: needsNormalisation,
    };
};
